import _ from "lodash";
import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import classNames from "classnames";
import {
  Badge,
  IconButton,
  SearchInput,
  Tabs,
  Tooltip,
} from "@toddle-edu/ds-web";
import { useDispatch } from "Hooks/useDispatch";

import {
  ExpandArrowsFourOutlined,
  AddOutlined,
  ArrowOutOutlined,
  CloseOutlined,
} from "@toddle-edu/ds-icons";

import Loading from "UIComponents/Loading";
import { useI18n } from "Hooks";
import {
  getIsCombineCurriculumHomePageEnabled,
  goToRoute,
} from "modules/NavigationModule";
import { compose, graphql } from "lib/apolloClient";
import { MESSAGING } from "Constants/stringConstants";
import {
  setMessagingUser,
  setMessagingChatId,
  setSelectedGlobalTrayIcon,
  setGlobalTrayRetracted,
} from "Login/modules/LoginModule";
import {
  getUserId,
  getSelectedCurriculumId,
  getIsStaff,
  getOrganizationId,
} from "modules/CommonSelectors";
import { getIsAdminTab } from "Announcements/modules/utils";
import { getConfigurationQuery } from "MessagingV2/components/Settings/modules/SettingQueries";

import {
  CHAT_SCREEN_MODE,
  CHAT_TYPE,
  isCreateAndManageOwnChannelEnabled,
  isInitiateMessageEnabled,
  recordMessagingWidgetExpand,
  isMessagingRevempEnabled,
  MESSAGING_V2_WIDGET_WRAPPER_ID,
  canShowChannel,
  isMessagingRevampV2Enabled,
  RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
  isMessagingV2Enabled,
  isMessagingRnpEnabled,
  isCreateChatsAndChannelsEnableForAll,
  getMessagingBaseRoute,
} from "MessagingV2/modules/Utils";
import {
  createPersonalChat,
  createFamilyChannel,
  setMessagingSections,
} from "MessagingV2/modules/MessagingModule";
import ChatOptionsProvider from "MessagingV2/providers/ChatOptionsProvider";
import ChatList from "MessagingV2/components/ThreadList/ChatList";
import ChannelList from "MessagingV2/components/ThreadList/ChannelList";
import {
  ThreadV2ListFilter,
  ThreadV2SearchFilter,
} from "MessagingV2/components/ThreadList/ThreadV2Filter";
import ChatAndChannelList from "MessagingV2/components/ThreadList/ChatAndChannelList";
import InitChatAndChannelButton from "MessagingV2/components/InitChatAndChannelButton";
import ChatContainer from "MessagingV2/components/ChatContainer";
import CreateNewChatModal from "MessagingV2/components/ThreadList/CreateNewChatModal";
import CreateChannelModal from "MessagingV2/components/Channels/CreateChannelModal";
import { getStaffRnpFilterCoursesCountQuery } from "Platform/routes/MessagingV2/modules/MessagingQuery";
import { MESSAGING_MODULE_GRADE_LEVEL_CONFIG } from "Platform/routes/MessagingV2/components/Settings/modules/SettingsUtils";
import { generateConfigurationQueryVariables } from "Login/components/ChildrenPage/modules/ChildrenPageUtils";
import { WithConfigurationsAndPreferences } from "Utils/ConfigurationUtils/hoc";
import { generateConfigurationQueryVariablesV2 } from "Utils/ConfigurationUtils/Utils";

import classes from "./MessagingModal.scss";

const ExpandIconForMessagingNavigation = ({
  goToRoute,
  selectedTab,
  setMessagingSections,
  selectedCurriculumId,
  isAdminTab,
  isAccountPortal,
}) => {
  const isUIRevempEnabled = isMessagingRevempEnabled();
  const isUIRevampEnabledV2 = isMessagingRevampV2Enabled();

  const { t } = useI18n(["common"]);
  const navigateToMessagingHomePage = () => {
    setMessagingSections({ section: selectedTab });
    const isCombinedCurriculumHomePage = getIsCombineCurriculumHomePageEnabled();
    const baseRoute = getMessagingBaseRoute({
      isCombinedCurriculumHomePage,
      isAdminTab,
      isAccountPortal,
      curriculumId: selectedCurriculumId,
    });
    const route = `${baseRoute}/messaging`;

    goToRoute({ route, isOpenInNewTab: isUIRevampEnabledV2 });
    recordMessagingWidgetExpand();
  };

  const showRevampedStyle = isUIRevempEnabled && !isUIRevampEnabledV2;

  return (
    <Tooltip tooltip={t("common:full_screen_mode")} placement="top">
      <IconButton
        icon={
          isUIRevampEnabledV2 ? (
            <ArrowOutOutlined />
          ) : (
            <ExpandArrowsFourOutlined />
          )
        }
        variant={showRevampedStyle ? "outlined-subtle" : "plain"}
        size={showRevampedStyle ? "small" : "medium"}
        className={classNames({
          [classes.iconButton]: showRevampedStyle,
        })}
        {...(isUIRevampEnabledV2 && { dsVersion: "2.0", type: "plain" })}
        onClick={navigateToMessagingHomePage}
      />
    </Tooltip>
  );
};

const CloseMessagingModalButton = () => {
  const isUIRevampEnabledV2 = isMessagingRevampV2Enabled();

  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  if (!isUIRevampEnabledV2) return null;

  const closeMessagingModal = () => {
    dispatch(setSelectedGlobalTrayIcon(""));
    dispatch(setGlobalTrayRetracted(true));
  };

  return (
    <Tooltip tooltip={t("common:close")} placement="top">
      <IconButton
        icon={<CloseOutlined />}
        variant="plain"
        size="medium"
        dsVersion="2.0"
        type="plain"
        onClick={closeMessagingModal}
      />
    </Tooltip>
  );
};

const ChatListContainer = ({ messagingChatId, onThreadClick, t }) => {
  const isUIRevempEnabled = isMessagingRevempEnabled();
  const [searchText, setSearchText] = useState("");

  const [isCreateNewChatModalOpen, setIsCreateNewChatModalOpen] = useState(
    false
  );

  const addNewChat = () => {
    setIsCreateNewChatModalOpen(true);
  };

  const closeCreateNewChatModal = () => {
    setIsCreateNewChatModalOpen(false);
  };

  const chatListInput = useMemo(
    () => ({ search: { text: _.trim(searchText) } }),
    [searchText]
  );

  const isInitiateChatEnable = isInitiateMessageEnabled();

  return (
    <>
      <div
        className={
          isUIRevempEnabled
            ? classes.messagingSearchBarV2
            : classes.messagingSearchBar
        }
      >
        <div className={classes.searchInputContainer}>
          <SearchInput
            value={searchText}
            placeholder={t("common:fina_a_label", {
              label: t("common:chat"),
            })}
            size={isUIRevempEnabled ? "medium" : "large"}
            onChange={setSearchText}
          />
        </div>
        {isInitiateChatEnable ? (
          <div
            className={isUIRevempEnabled ? classes.iconBtnV2 : classes.iconBtn}
          >
            <IconButton
              icon={<AddOutlined />}
              size="medium"
              onClick={addNewChat}
              variant="progressive"
            />
          </div>
        ) : null}
      </div>

      <div className={classes.messagingChatList}>
        <ChatList
          chatId={messagingChatId}
          showUserTypeFilterAndInitiateChat={false}
          onThreadClick={onThreadClick}
          input={chatListInput}
        />
      </div>
      {isCreateNewChatModalOpen ? (
        <CreateNewChatModal onCloseModal={closeCreateNewChatModal} />
      ) : null}
    </>
  );
};

const ChannelListContainer = ({ messagingChatId, onThreadClick, t }) => {
  const isUIRevempEnabled = isMessagingRevempEnabled();
  const [channelSearchText, setChannelSearchText] = useState("");

  const [
    isOpenCreateNewChannelModal,
    setIsOpenCreateNewChannelModal,
  ] = useState(false);

  const addNewChannel = () => {
    setIsOpenCreateNewChannelModal(true);
  };

  const closeCreateNewChannelModal = () => {
    setIsOpenCreateNewChannelModal(false);
  };

  const canCreateChannel = isCreateAndManageOwnChannelEnabled();

  return (
    <>
      <div
        className={
          isUIRevempEnabled
            ? classes.messagingSearchBarV2
            : classes.messagingSearchBar
        }
      >
        <div className={classes.searchInputContainer}>
          <SearchInput
            value={channelSearchText}
            placeholder={t("common:fina_a_label", {
              label: t("common:channel"),
            })}
            size={isUIRevempEnabled ? "medium" : "large"}
            onChange={setChannelSearchText}
          />
        </div>
        {canCreateChannel ? (
          <div
            className={isUIRevempEnabled ? classes.iconBtnV2 : classes.iconBtn}
          >
            <IconButton
              icon={<AddOutlined />}
              size="medium"
              onClick={addNewChannel}
              variant="progressive"
            />
          </div>
        ) : null}
      </div>
      <div className={classes.messagingChatList}>
        <ChannelList
          chatId={messagingChatId}
          showChannelTypeFilter={false}
          onThreadClick={onThreadClick}
          searchText={_.trim(channelSearchText)}
        />
      </div>
      {isOpenCreateNewChannelModal ? (
        <CreateChannelModal onCloseModal={closeCreateNewChannelModal} />
      ) : null}
    </>
  );
};

const MessagingModal = props => {
  const {
    goToRoute,
    messagingUser,
    messagingChatId,
    setMessagingUser,
    setMessagingChatId,
    setSelectedGlobalTrayIcon,
    createPersonalChat,
    createFamilyChannel,
    unreadChatCounts = {},
    isMessagingLoading,
    setMessagingSections,
    selectedCurriculumId,
    isAdminTab,
    toggleShowMessagingModal,
    isLoading: isQueryLoading,
    isData,
  } = props;

  const isUIRevempEnabled = isMessagingRevempEnabled();
  const [selectedTab, setSelectedTab] = useState(CHAT_TYPE.CHAT);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useI18n(["common"]);

  const tabsRef = useRef(null);

  useEffect(() => {
    // Used for creating a personal chat
    // from outside of the widget
    // e.g. Global access points

    const { id: messagingUserId, isFamilyChannel } = messagingUser || {};
    if (messagingUserId) {
      const createPersonalChatHandler = async () => {
        setSelectedGlobalTrayIcon(MESSAGING);
        setIsLoading(true);

        const chat = !isFamilyChannel
          ? await createPersonalChat(messagingUserId)
          : await createFamilyChannel(messagingUserId);

        if (chat) {
          setMessagingChatId(chat);
        }
        setIsLoading(false);
        setMessagingUser({
          id: null,
        });
      };

      createPersonalChatHandler();
    }

    return () => {
      toggleShowMessagingModal();
    };
  }, [messagingUser]);

  const anyChatSelected = !_.isNull(messagingChatId);
  const currentUserId = getUserId();
  const { chatUnreadCount = 0, channelUnreadCount = 0 } = unreadChatCounts;

  const tabOptions = [
    {
      label: t("common:chats"),
      value: CHAT_TYPE.CHAT,
      suffix: <Badge size="medium" value={chatUnreadCount} />,
    },
    ...(canShowChannel()
      ? [
          {
            label: t("common:channels"),
            value: CHAT_TYPE.CHANNEL,
            suffix: <Badge size="medium" value={channelUnreadCount} />,
          },
        ]
      : []),
  ];

  const onThreadClick = useCallback(id => setMessagingChatId(id), [
    setMessagingChatId,
  ]);

  const onBackClickFromWidget = useCallback(() => {
    setMessagingChatId(null);
  }, [setMessagingChatId]);

  const revampV2Enabled = isMessagingRevampV2Enabled();

  if (!isData && !isQueryLoading) return null;

  return createPortal(
    <ChatOptionsProvider
      chatId={messagingChatId}
      activeUserId={currentUserId}
      screenMode={CHAT_SCREEN_MODE.WIDGET}
    >
      <div
        className={classes.messagingModalContent}
        id={MESSAGING_V2_WIDGET_WRAPPER_ID}
      >
        {!(isLoading || isMessagingLoading || isQueryLoading) ? (
          anyChatSelected ? (
            <ChatContainer
              chatId={messagingChatId}
              onBackClickFromWidget={onBackClickFromWidget}
            />
          ) : (
            <>
              <div
                className={classNames(classes.messagingModalHeader, {
                  [classes.messagingModalHeaderV2]: revampV2Enabled,
                })}
              >
                <h6
                  className={classNames({
                    ["heading-5"]: !revampV2Enabled,
                  })}
                >
                  {t("common:messaging")}
                </h6>
                <div className={classes.messagingModalHeaderActions}>
                  <ExpandIconForMessagingNavigation
                    goToRoute={goToRoute}
                    selectedTab={selectedTab}
                    setMessagingSections={setMessagingSections}
                    selectedCurriculumId={selectedCurriculumId}
                    isAdminTab={isAdminTab}
                  />
                  <CloseMessagingModalButton />
                </div>
              </div>
              {!revampV2Enabled ? (
                <div
                  className={
                    isUIRevempEnabled
                      ? classes.messagingModalTabsV2
                      : classes.messagingModalTabs
                  }
                  ref={tabsRef}
                >
                  <Tabs
                    options={tabOptions}
                    value={selectedTab}
                    onChange={setSelectedTab}
                    wrapperRef={tabsRef}
                    type="fixed"
                  />
                </div>
              ) : (
                <div className={classes.threadV2HeaderContainer}>
                  <div className={classes.searchAndInitChatContainer}>
                    <ThreadV2SearchFilter />
                    <InitChatAndChannelButton />
                  </div>
                  <ThreadV2ListFilter showManageFilter={false} />
                </div>
              )}
              {!revampV2Enabled ? (
                selectedTab === CHAT_TYPE.CHAT ? (
                  <ChatListContainer
                    messagingChatId={messagingChatId}
                    onThreadClick={onThreadClick}
                    t={t}
                  />
                ) : (
                  <ChannelListContainer
                    messagingChatId={messagingChatId}
                    onThreadClick={onThreadClick}
                    t={t}
                  />
                )
              ) : (
                <div className={classes.messagingChatList}>
                  <ChatAndChannelList
                    onThreadClick={onThreadClick}
                    showFilterTitle
                    showSuggestions
                  />
                </div>
              )}
            </>
          )
        ) : (
          <Loading />
        )}
      </div>
    </ChatOptionsProvider>,
    document.body
  );
};

const mapStateToProps = state => {
  const messagingUser = _.get(state, "login.messagingUser");
  const messagingChatId = _.get(state, "login.messagingChatId");

  const isMessagingLoading = _.get(state, "messagingV2.isLoading");
  const isAccountPortal =
    _.get(state, "login.activeTab") === "accountSettingsTab";

  return {
    messagingUser,
    messagingChatId,
    isMessagingLoading,
    isAccountPortal,
    selectedCurriculumId: getSelectedCurriculumId(state),
    isAdminTab: getIsAdminTab(state),
    isData: true,
    isLoading: false,
    organizationId: getOrganizationId(state),
  };
};

const mapActionCreators = {
  goToRoute,
  setMessagingUser,
  setMessagingChatId,
  setSelectedGlobalTrayIcon,
  createPersonalChat,
  createFamilyChannel,
  setMessagingSections,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getConfigurationQuery, {
    name: "getConfiguration",
    skip: () => !getIsStaff(),
    options: () => {
      return {
        variables: generateConfigurationQueryVariables({
          src: "ORGANIZATION_LEVEL",
          organizationId: getOrganizationId(),
          configurations: MESSAGING_MODULE_GRADE_LEVEL_CONFIG,
        }),
      };
    },
    props: ({ getConfiguration, ownProps: { isLoading, isData } }) => {
      const configurations = _.get(
        getConfiguration,
        "platform.configuration.0.configurations",
        []
      );
      return {
        isData: isData && !_.isEmpty(configurations),
        isLoading:
          _.includes([1, 2], _.get(getConfiguration, "networkStatus")) ||
          isLoading,
      };
    },
  }),
  graphql(getStaffRnpFilterCoursesCountQuery, {
    name: "getStaffRnpFilterCoursesCount",
    skip: () =>
      !isMessagingV2Enabled() ||
      !isMessagingRnpEnabled() ||
      !isCreateChatsAndChannelsEnableForAll(),
    options: () => ({
      fetchPolicy: "cache-and-network",
      variables: {
        userId: getUserId(),
        filters: {
          fetchOnlyMappedCourses: false,
          controlParameters: [
            RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
          ],
        },
      },
    }),
    props({ getStaffRnpFilterCoursesCount, ownProps: { isLoading, isData } }) {
      const node = _.get(getStaffRnpFilterCoursesCount, "node");
      return {
        isData: isData && !_.isEmpty(node),
        isLoading:
          _.includes(
            [1, 2],
            _.get(getStaffRnpFilterCoursesCount, "networkStatus")
          ) || isLoading,
      };
    },
  }),
  WithConfigurationsAndPreferences({
    skip: ({ organizationId }) => !organizationId,
    options: ({ organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        src: "ORGANIZATION_LEVEL",
        organizationId,
        modules: ["MESSAGING"],
      }),
    }),
    getConfigurationKeys: () => ["ORGANIZATION"],
  })
)(MessagingModal);
